import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";
import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  Edit2,
  Plus,
  Shield,
  Trash2,
  Users,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createCoachAccount } from "../../lib/auth";
import type { SubscriptionTier } from "../../types/auth";
import { auth, db } from "../../lib/firebase";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { doc, updateDoc } from "firebase/firestore";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

// Create a separate Firebase app instance for coach creation
const coachAuthApp = initializeApp( 
  {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  },
  "coachAuth"
);

// Get auth instance for coach creation
const coachAuth = getAuth(coachAuthApp);

interface Coach {
  id: string;
  userId: string;
  assessmentCode: string;
  tier: string;
  aiAnalysisAccess: boolean;
  manualAiAccess?: boolean;
  user: {
    fullName: string;
    email: string;
  };
  stats: {
    totalRespondents: number;
    completedAssessments: number;
  };
}

const columnHelper = createColumnHelper<Coach>();

export function CoachManagement() {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [newCoach, setNewCoach] = useState({
    email: "",
    password: "",
    fullName: "",
    tier: "basic" as SubscriptionTier,
  });

  const [editForm, setEditForm] = useState({
    tier: "basic" as SubscriptionTier,
    aiAnalysisAccess: false,
    manualAiAccess: false,
  });

  const [globalFilter, setGlobalFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [aiFilter, setAiFilter] = useState("all");

  const columns = useMemo(() => [
    columnHelper.accessor('user.fullName', {
      header: 'Name',
      cell: (info) => (
        <div className="font-medium text-gray-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('user.email', {
      header: 'Email',
      cell: (info) => (
        <div className="text-gray-600">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('assessmentCode', {
      header: 'Assessment Code',
      cell: (info) => (
        <div className="font-mono text-sm text-gray-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('tier', {
      header: 'Tier',
      cell: (info) => (
        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
          {info.getValue().replace('_', ' ')}
        </span>
      ),
    }),
    columnHelper.accessor('stats.totalRespondents', {
      header: 'Respondents',
      cell: (info) => (
        <div className="text-gray-900 font-medium">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('stats.completedAssessments', {
      header: 'Completed',
      cell: (info) => (
        <div className="text-gray-900 font-medium">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('aiAnalysisAccess', {
      header: 'AI Access',
      cell: (info) => {
        const coach = info.row.original;
        const hasAnyAI = coach.aiAnalysisAccess || coach.manualAiAccess;
        return (
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            hasAnyAI
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {hasAnyAI ? 'Enabled' : 'Disabled'}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const coach = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEditClick(coach)}
              className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all"
              title="Edit Coach"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => {/* Handle delete */}}
              className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all"
              title="Delete Coach"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      },
    }),
  ], []);

  const filteredData = useMemo(() => {
    return coaches.filter(coach => {
      // Tier filter
      if (tierFilter !== "all" && coach.tier !== tierFilter) {
        return false;
      }
      
      // AI filter
      if (aiFilter === "enabled" && !coach.aiAnalysisAccess && !coach.manualAiAccess) {
        return false;
      }
      if (aiFilter === "disabled" && (coach.aiAnalysisAccess || coach.manualAiAccess)) {
        return false;
      }
      
      return true;
    });
  }, [coaches, tierFilter, aiFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user?.uid) {
        loadCoaches();
      }
    });

    return () => unsubscribe();
  }, []);

  const loadCoaches = async () => {
    try {
      setIsLoading(true);
      const coachesRef = collection(db, "coaches");
      const coachesSnapshot = await getDocs(coachesRef);

      const coachesData = await Promise.all(
        coachesSnapshot.docs.map(async (doc) => {
          const data = doc.data();

          // Get user data
          const userDoc = await getDocs(
            query(collection(db, "users"), where("userId", "==", data.userId))
          );
          const userData = userDoc.docs[0]?.data();

          // Get stats
          const respondentsQuery = query(
            collection(db, "respondents"),
            where("coachId", "==", data.userId)
          );
          const respondentsSnapshot = await getDocs(respondentsQuery);

          const completedQuery = query(
            collection(db, "results"),
            where("coachId", "==", data.userId)
          );
          const completedSnapshot = await getDocs(completedQuery);

          return {
            id: doc.id,
            userId: data.userId,
            assessmentCode: data.assessmentCode,
            tier: data.tier,
            aiAnalysisAccess: data.aiAnalysisAccess,
            manualAiAccess: data.manualAiAccess,
            user: {
              fullName: userData?.fullName || "",
              email: userData?.email || "",
            },
            stats: {
              totalRespondents: respondentsSnapshot.size,
              completedAssessments: completedSnapshot.size,
            },
          };
        })
      );

      setCoaches(coachesData);
    } catch (err) {
      console.error("Error loading coaches:", err);
      setError("Failed to load coaches");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCoach = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      const coachId = await createCoachAccount(coachAuth, {
        ...newCoach,
        assessmentCode: generateAssessmentCode(),
        partnerId: "",
      });

      setShowAddModal(false);
      loadCoaches();

      // Show success message with credentials
      alert(
        `Coach account created successfully!\n\nEmail: ${newCoach.email}\nPassword: ${newCoach.password}\n\nPlease share these credentials with the coach securely.`
      );
    } catch (err) {
      console.error("Error adding coach:", err);
      setError("Failed to add coach");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (coach: Coach) => {
    setSelectedCoach(coach);
    setEditForm({
      tier: coach.tier as SubscriptionTier,
      aiAnalysisAccess: coach.aiAnalysisAccess,
      manualAiAccess: coach.manualAiAccess || false,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCoach) return;

    try {
      setIsLoading(true);
      setError("");

      // Update coach document
      const coachRef = doc(db, "coaches", selectedCoach.id);
      await updateDoc(coachRef, {
        tier: editForm.tier,
        aiAnalysisAccess: ["advanced", "partner"].includes(editForm.tier),
        manualAiAccess: editForm.tier === "basic_plus",
        updatedAt: new Date(),
      });

      setShowEditModal(false);
      await loadCoaches();
    } catch (err) {
      console.error("Error updating coach:", err);
      setError("Failed to update coach");
    } finally {
      setIsLoading(false);
    }
  };

  const generateAssessmentCode = () => {
    return "PBP" + Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading coaches..." />;
  }

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Users className="w-12 h-12 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Coach Management
              </h1>
              <p className="text-gray-600">Create and manage coach accounts</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r 
              from-blue-500 to-indigo-600 text-white font-medium hover:scale-105 transition-all"
          >
            <Plus className="w-5 h-5" />
            Add Coach
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm border border-gray-200">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search coaches..."
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              />
            </div>
            
            <div>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              >
                <option value="all">All Tiers</option>
                <option value="basic">Basic</option>
                <option value="basic_plus">Basic Plus</option>
                <option value="advanced">Advanced</option>
                <option value="partner">Partner</option>
              </select>
            </div>
            
            <div>
              <select
                value={aiFilter}
                onChange={(e) => setAiFilter(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              >
                <option value="all">All AI Status</option>
                <option value="enabled">AI Enabled</option>
                <option value="disabled">AI Disabled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th 
                        key={header.id}
                        className="px-6 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      {coaches.length === 0 ? "No coaches found" : "No coaches match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <motion.tr
                      key={row.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{' '}
                  of {table.getFilteredRowModel().rows.length} results
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      table.setPageIndex(page);
                    }}
                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                    min="1"
                    max={table.getPageCount()}
                  />
                  <span className="text-sm text-gray-600">of {table.getPageCount()}</span>
                </div>
                
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
                
                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {[5, 10, 20, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Add Coach Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-8 w-full max-w-lg mx-4 shadow-xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Coach</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleAddCoach} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-gray-900 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    value={newCoach.fullName}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        fullName: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-900 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newCoach.email}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-gray-900 mb-2">
                    Initial Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={newCoach.password}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    required
                    minLength={8}
                    placeholder="Minimum 8 characters"
                  />
                </div>

                <div>
                  <label htmlFor="tier" className="block text-gray-900 mb-2">
                    Subscription Tier
                  </label>
                  <select
                    id="tier"
                    name="tier"
                    value={newCoach.tier}
                    onChange={(e) =>
                      setNewCoach((prev) => ({
                        ...prev,
                        tier: e.target.value as SubscriptionTier,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    disabled={isLoading}
                  >
                    <option value="basic">Basic</option>
                    <option value="basic_plus">Basic Plus</option>
                    <option value="advanced">Advanced</option>
                    <option value="partner">Partner</option>
                  </select>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 
                      text-white font-medium hover:scale-105 transition-all disabled:opacity-50
                      flex items-center gap-2"
                  >
                    <Shield className="w-5 h-5" />
                    {isLoading ? "Adding..." : "Add Coach"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Edit Coach Modal */}
        {showEditModal && selectedCoach && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-8 w-full max-w-lg mx-4 shadow-xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Coach</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {selectedCoach.user.fullName}
                </h3>
                <p className="text-gray-600">{selectedCoach.user.email}</p>
                <p className="text-sm text-gray-500">Code: {selectedCoach.assessmentCode}</p>
              </div>

              <form onSubmit={handleEditSubmit} className="space-y-6">
                <div>
                  <label htmlFor="editTier" className="block text-gray-900 mb-2">
                    Subscription Tier
                  </label>
                  <select
                    id="editTier"
                    value={editForm.tier}
                    onChange={(e) => setEditForm(prev => ({ ...prev, tier: e.target.value as SubscriptionTier }))}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    disabled={isLoading}
                  >
                    <option value="basic">Basic</option>
                    <option value="basic_plus">Basic Plus</option>
                    <option value="advanced">Advanced</option>
                    <option value="partner">Partner</option>
                  </select>
                </div>

                <div className="bg-blue-50 p-4 rounded-xl">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Access Levels:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>Basic:</strong> No AI access</li>
                    <li>• <strong>Basic Plus:</strong> Manual AI analysis only</li>
                    <li>• <strong>Advanced:</strong> Full AI analysis access</li>
                    <li>• <strong>Partner:</strong> Full AI analysis access</li>
                  </ul>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 
                      text-white font-medium hover:scale-105 transition-all disabled:opacity-50
                      flex items-center gap-2"
                  >
                    <Shield className="w-5 h-5" />
                    {isLoading ? "Updating..." : "Update Coach"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}